import React from "react";
import { useLanguage } from "../context/LanguageContext";
import translations from "../i18n/translations";

const ProjectCard = ({ project }) => {
  const { language } = useLanguage();
  const t = translations[language];

  const {
    id,
    title,
    description,
    image,
    qrImage,
    demoUrl,
    liveUrl,
    sourceUrl,
    tags = [],
  } = project;

  const projectContent = t.projectsContent?.[id];
  const titleToShow = projectContent?.title || title;
  const descriptionToShow = projectContent?.description || description;
  const tagsToShow = projectContent?.tags || tags;
  const demoLabel = t.projectsButtons?.demo || "Demo";
  const liveLabel = t.projectsButtons?.live || "Live";
  const codeLabel = t.projectsButtons?.code || "Code";
  const isLogoProject = id === 23 || id === 24 || id === 25;

  return (
    <article className="project-desc flex h-full flex-col overflow-hidden rounded-lg bg-gray-900 shadow-lg ring-1 ring-gray-800 transition-shadow duration-500 hover:shadow-[0_0_30px_-8px_rgba(100,255,218,0.35)] hover:ring-coding-color/40">
      {image &&
        (isLogoProject ? (
          <div className="flex h-48 w-full items-center justify-center bg-white">
            <img
              className="max-h-32 w-auto object-contain"
              src={image}
              alt={titleToShow}
              loading="lazy"
            />
          </div>
        ) : qrImage ? (
          <div className="grid h-48 w-full grid-cols-[minmax(0,1fr)_7.5rem] overflow-hidden bg-slate-950 sm:grid-cols-[minmax(0,1fr)_8.5rem]">
            <img
              className="h-full w-full object-cover"
              src={image}
              alt={titleToShow}
              loading="lazy"
            />
            <div className="flex h-full flex-col items-center justify-center gap-2 border-l border-white/10 bg-white p-3">
              <img
                className="aspect-square w-full max-w-24 object-contain"
                src={qrImage}
                alt={`QR code for ${titleToShow}`}
                loading="lazy"
              />
              <span className="text-center text-[10px] font-semibold uppercase leading-tight tracking-wide text-gray-700">
                Scan demo
              </span>
            </div>
          </div>
        ) : (
          <img
            className="h-48 w-full object-cover"
            src={image}
            alt={titleToShow}
            loading="lazy"
          />
        ))}
      <div className="flex flex-1 flex-col px-5 py-4">
        <h3 className="mb-2 text-lg font-semibold text-white">
          {titleToShow}
        </h3>
        <p className="flex-1 text-sm text-gray-300">{descriptionToShow}</p>
        {tagsToShow.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {tagsToShow.map((tag) => (
              <span
                key={tag}
                className="rounded-full bg-gray-800 px-2 py-1 text-xs text-coding-color"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
        <div className="mt-4 flex gap-3">
          {demoUrl && (
            <a
              href={demoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-full bg-emerald-500 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white hover:bg-emerald-600"
            >
              {demoLabel}
            </a>
          )}
          {liveUrl && (
            <a
              href={liveUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-full bg-blue-500 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white hover:bg-blue-600"
            >
              {liveLabel}
            </a>
          )}
          {sourceUrl && (
            <a
              href={sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-full bg-gray-700 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white hover:bg-gray-600"
            >
              {codeLabel}
            </a>
          )}
        </div>
      </div>
    </article>
  );
};

export default ProjectCard;
